import * as React from 'react';

import { cn } from '../../../lib';
import { Group } from '@folio/components/ui/flex';

type NumberQuickOptionsProps = {
  min: number;
  max: number;
  labels?: Record<number, string>;
  otherInitialValue?: number;
  value: number | null | undefined;
  onChange: (value: number) => void;
  disabled?: boolean;
  hideOther?: boolean;
  name?: string;
};

export const NumberQuickOptions = ({
  min,
  max,
  labels,
  otherInitialValue,
  value,
  onChange,
  disabled,
  hideOther = false,
  name,
}: NumberQuickOptionsProps) => {
  const otherInit = otherInitialValue ?? max + 1;
  const numValue = value === null || value === undefined ? null : Number(value);
  const isInRange = numValue !== null && numValue >= min && numValue <= max;
  const [showCustom, setShowCustom] = React.useState(numValue !== null && !isInRange);

  const options = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  const chipClass = (selected: boolean) =>
    cn(
      'h-9 min-w-[2.5rem] cursor-pointer rounded-full border-2 px-3 text-sm font-medium transition-all duration-200',
      selected
        ? 'border-primary bg-primary/5 text-primary shadow-sm'
        : 'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
      disabled && 'cursor-not-allowed opacity-50',
    );

  const stepBtnClass = (inactive: boolean) =>
    cn(
      'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 text-sm font-medium transition-all duration-200',
      'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
      inactive && 'cursor-not-allowed opacity-50',
    );

  const handleDecrement = () => {
    const current = numValue ?? otherInit;
    if (current <= min) return;
    const next = current - 1;
    if (next >= min && next <= max) {
      setShowCustom(false);
    }
    onChange(next);
  };

  const handleIncrement = () => {
    onChange((numValue ?? otherInit) + 1);
  };

  return (
    <Group role="radiogroup" aria-label={name} wrap gap={2}>
      {options.map(n => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={!showCustom && numValue === n}
          disabled={disabled}
          onClick={() => {
            setShowCustom(false);
            onChange(n);
          }}
          className={chipClass(!showCustom && numValue === n)}
        >
          {labels?.[n] ?? n}
        </button>
      ))}
      {!hideOther && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (showCustom) return;
            setShowCustom(true);
            onChange(isInRange || numValue === null ? otherInit : numValue);
          }}
          className={chipClass(showCustom)}
        >
          Autre
        </button>
      )}
      {!hideOther && showCustom && (
        <Group gap={2}>
          <button
            type="button"
            disabled={disabled || (numValue ?? otherInit) <= min}
            onClick={handleDecrement}
            className={stepBtnClass(!!(disabled || (numValue ?? otherInit) <= min))}
          >
            {'-'}
          </button>
          <input
            type="number"
            value={numValue ?? otherInit}
            onChange={e => {
              if (e.target.value !== '') onChange(Number(e.target.value));
            }}
            onWheel={e => e.currentTarget.blur()}
            disabled={disabled}
            className="flex h-10 w-24 rounded-lg border border-input bg-background px-3 py-2 text-center text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-95 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <button
            type="button"
            disabled={disabled}
            onClick={handleIncrement}
            className={stepBtnClass(!!disabled)}
          >
            {'+'}
          </button>
        </Group>
      )}
    </Group>
  );
};
